import { parseAsString, useQueryState } from 'nuqs';
import { createParser } from 'nuqs';

type ReadinessSubTab = 'individual' | 'overview' | 'projection';

const parseAsReadinessSubTab = createParser<ReadinessSubTab>({
  parse: (value) => {
    if (value === 'overview' || value === 'individual' || value === 'projection') return value;
    return null;
  },
  serialize: (value) => value,
});

type UseReadinessTabConfig = {
  assessmentIdParam: string;
  tabParam: string;
};

type UseReadinessTabReturn = {
  activeTab: ReadinessSubTab;
  assessmentId: string | null;
  selectAssessment: (id: string | null) => void;
  setActiveTab: (tab: ReadinessSubTab) => void;
};

/**
 * Generic hook for managing TRL/MRL tab state via URL query params.
 *
 * @param config - Configuration specifying query param names
 * @returns Tab state and handlers
 *
 * @example
 * // For TRL tab
 * const { activeTab, assessmentId, selectAssessment, setActiveTab } = useReadinessTab({
 *   tabParam: 'trlTab',
 *   assessmentIdParam: 'assessmentId',
 * });
 *
 * // For MRL tab
 * const { activeTab, assessmentId, selectAssessment, setActiveTab } = useReadinessTab({
 *   tabParam: 'mrlTab',
 *   assessmentIdParam: 'mrlAssessmentId',
 * });
 */
export function useReadinessTab(config: UseReadinessTabConfig): UseReadinessTabReturn {
  const { assessmentIdParam, tabParam } = config;

  const [tab, setTab] = useQueryState(tabParam, parseAsReadinessSubTab.withDefault('overview'));
  const [assessmentId, setAssessmentId] = useQueryState(assessmentIdParam, parseAsString);

  const selectAssessment = (id: string | null) => {
    void setTab('individual');
    void setAssessmentId(id);
  };

  const switchToOverview = () => {
    void setTab('overview');
    void setAssessmentId(null);
  };

  const setActiveTab = (newTab: ReadinessSubTab) => {
    if (newTab === 'overview') {
      switchToOverview();
    } else if (newTab === 'individual') {
      void setTab('individual');
      void setAssessmentId(null);
    } else {
      void setTab('projection');
      void setAssessmentId(null);
    }
  };

  return {
    activeTab: tab,
    assessmentId,
    selectAssessment,
    setActiveTab,
  };
}

export type { ReadinessSubTab, UseReadinessTabConfig, UseReadinessTabReturn };
